import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { PlatformOverview } from '@barbeiropro/contracts';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import { Card, dd, Kpis } from './shared';

export function OverviewPage() {
  const q = useQuery({ queryKey: ['platform', 'overview'], queryFn: () => api.get<PlatformOverview>('/platform/overview') });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const o = q.data;
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader title="Visão geral" description="Situação das barbearias clientes e da receita recorrente. Valores de MRR são contratados; veja Métricas para as fórmulas." />
      <Kpis
        items={[
          { label: 'Barbearias', value: String(o.orgsTotal), hint: `${o.orgsActive} ativas, ${o.orgsSuspended} suspensas` },
          { label: 'MRR contratado', value: money(o.mrrCents), hint: `ARR ${money(o.arrCents)}` },
          { label: 'Recebido no mês', value: money(o.receivedThisMonthCents), hint: 'Faturas pagas (realizado)' },
          { label: 'Novas em 30 dias', value: String(o.newOrgs30d) },
          { label: 'Em teste', value: String(o.trialing) },
          { label: 'Inadimplentes', value: String(o.pastDueOrGrace), hint: 'Atrasadas ou em carência', tone: o.pastDueOrGrace ? 'warn' : undefined },
          { label: 'Eventos com falha', value: String(o.failedEvents), hint: 'Webhooks de cobrança', tone: o.failedEvents ? 'bad' : undefined },
          { label: 'Chamados abertos', value: String(o.openTickets) },
        ]}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Testes terminando" description="Próximos vencimentos de período de teste.">
          {o.expiringTrials.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum teste termina em breve.</p>
          ) : (
            <ul className="divide-y">
              {o.expiringTrials.map((t) => (
                <li key={t.organizationId} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <Link to={`/plataforma/organizacoes/${t.organizationId}`} className="min-w-0 truncate font-medium hover:underline">
                    {t.organizationName}
                  </Link>
                  <span className="tabular shrink-0 text-muted-foreground">{dd(t.trialEndsAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Cadastros recentes">
          {o.recentSignups.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum cadastro ainda.</p>
          ) : (
            <ul className="divide-y">
              {o.recentSignups.map((s) => (
                <li key={s.organizationId} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <Link to={`/plataforma/organizacoes/${s.organizationId}`} className="min-w-0 truncate font-medium hover:underline">
                    {s.name}
                  </Link>
                  <span className="shrink-0 text-muted-foreground">
                    {s.planName ?? 'Sem plano'} · {dd(s.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
